import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { trackGetStartedClick } from '../utils/analytics';
import ScheduleDemoModal from './ScheduleDemoModal';
import ContactSalesModal from './ContactSalesModal';
import ContactSupportModal from './ContactSupportModal';
import './Navigation.css';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showScheduleDemoModal, setShowScheduleDemoModal] = useState(false);
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);
  const [showContactSupportModal, setShowContactSupportModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(() => {
    // Pick up state from vanilla JS handler if user opened menu before React hydrated
    const menu = typeof document !== 'undefined' && document.querySelector('.mobile-menu');
    return menu ? menu.classList.contains('open') : false;
  });
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isDarkHeroPage = /^\/use-cases\/.+/.test(location.pathname);

  // Redirect to testmindfrontend (app.qate.ai) for signup
  const saasUrl = process.env.REACT_APP_SAAS_URL || 'https://app.qate.ai';

  useEffect(() => {
    let ticking = false;
    let lastScrolled = false;

    // Hysteresis thresholds to prevent rapid toggling near the boundary
    const SCROLL_DOWN_THRESHOLD = 100;
    const SCROLL_UP_THRESHOLD = 50; // Must scroll back up further to un-trigger

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          let shouldBeScrolled;

          // Use hysteresis: different thresholds for scrolling up vs down
          if (lastScrolled) {
            // Currently in scrolled state - only exit if we scroll back up past lower threshold
            shouldBeScrolled = scrollY > SCROLL_UP_THRESHOLD;
          } else {
            // Currently in non-scrolled state - only enter if we scroll past higher threshold
            shouldBeScrolled = scrollY > SCROLL_DOWN_THRESHOLD;
          }

          if (shouldBeScrolled !== lastScrolled) {
            lastScrolled = shouldBeScrolled;
            setScrolled(shouldBeScrolled);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const mobileMenu = document.querySelector('.mobile-menu');
      const hamburger = document.querySelector('.hamburger-menu');
      
      if (isMobileMenuOpen && 
          mobileMenu && 
          !mobileMenu.contains(event.target) && 
          hamburger && 
          !hamburger.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const isMobile = window.innerWidth <= 768;
      const headerOffset = isMobile ? 120 : 80; // Mobile header is taller
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
    if (isHomePage) {
      scrollToSection(sectionId);
    } else {
      // If not on homepage, navigate to homepage then scroll
      window.location.href = `/#${sectionId}`;
    }
  };

  const handleCTAClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false); // Close mobile menu
    trackGetStartedClick('navigation');
    window.gtag_report_conversion(`${saasUrl}/get-started`);
  };

  const handleScheduleDemoClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setShowScheduleDemoModal(true);
  };

  const handleContactSalesClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setShowContactSalesModal(true);
  };

  const handleContactSupportClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setShowContactSupportModal(true);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignInClick = () => {
    setIsMobileMenuOpen(false); // Close mobile menu
    window.open('https://app.qate.ai', '_blank');
  };

  const handleDocumentationClick = (section = null) => {
    setIsMobileMenuOpen(false); // Close mobile menu
    const url = section ? `/documentation#${section}` : '/documentation';
    window.open(url, '_blank');
  };

  return (
    <>
      <div className={`top-banner ${scrolled ? 'hidden' : ''}`}>
        <span className="top-banner-text-full">Let QATE discover your app and build a map and knowledge base in minutes: </span>
        <span className="top-banner-text-short">Discover your app in minutes: </span>
        <a href="#state-explorer" className="top-banner-link">Discover Now →</a>
      </div>
      <nav className={`navigation ${scrolled ? 'scrolled' : ''} ${isDarkHeroPage && !scrolled ? 'dark-hero' : ''}`}>
        <div className="nav-container">
          <div className="nav-top-row">
          <Link to="/" className="logo">
            <div className="logo-icon-wrapper">
              <img src="/qatelogo.png" alt="Q" className="logo-q-icon" width="30" height="30" />
            </div>
            <div className="logo-text">
              <div>
                <span className="logo-test">QATE&nbsp;</span>
                <span className="logo-agents">AI</span>
              </div>
              <span className="logo-subtext">quality&nbsp; assurance</span>
            </div>
          </Link>

            
            {/* Desktop Navigation */}
            <ul className="nav-links desktop-nav">
              {isHomePage ? (
                // On homepage, use smooth scroll
                <>
                  <li className="nav-dropdown">
                    <a href="/#features" onClick={(e) => handleNavClick(e, 'features')}>Features <span className="dropdown-arrow">▾</span></a>
                    <div className="nav-dropdown-menu">
                      <button type="button" onClick={(e) => handleNavClick(e, 'conversational-exploration')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 9h8M8 13h4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Conversational Test Creation</span><span className="dropdown-item-subtitle">Chat naturally to create tests</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'windows-desktop')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Windows Desktop Testing</span><span className="dropdown-item-subtitle">Test native Windows applications</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'ai-suggestions')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Test Suggestions</span><span className="dropdown-item-subtitle">Smart recommendations for coverage</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'discovery')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Discovery</span><span className="dropdown-item-subtitle">Auto-map your application</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'ai-test-healing')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Test Healing</span><span className="dropdown-item-subtitle">Self-repairing test automation</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'ai-bug-detection')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2l1.88 1.88M14.12 3.88L16 2M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"/><path d="M12 20c-3.3 0-6-2.7-6-6v-3a6 6 0 0 1 12 0v3c0 3.3-2.7 6-6 6Z"/><path d="M12 20v-9M6.53 9C4.6 8.8 3 7.1 3 5M6 13H3M6 17l-3 1M17.47 9c1.93-.2 3.53-1.9 3.53-4M18 13h3M18 17l3 1"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Bug Detection</span><span className="dropdown-item-subtitle">Automatically find issues</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'ai-bug-solving')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Bug Solving</span><span className="dropdown-item-subtitle">Get fix suggestions instantly</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'github-copilot')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/><circle cx="12" cy="12" r="4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Coding Agent</span><span className="dropdown-item-subtitle">Copilot & Duo bugfix automation</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'pr-processing')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M6 21V9a9 9 0 0 0 9 9"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Smart PR / MR Processing</span><span className="dropdown-item-subtitle">AI-driven test impact analysis</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'knowledge-base')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8M8 11h8M8 15h4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Knowledge Base</span><span className="dropdown-item-subtitle">Centralized app understanding</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'mfa-totp')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Multi-Factor Auth</span><span className="dropdown-item-subtitle">TOTP & authenticator testing</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'enterprise-integrations')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Enterprise Integrations</span><span className="dropdown-item-subtitle">Connect your existing tools</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'reports-insights')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3v18h18"/><path d="M18 17V9M13 17V5M8 17v-3"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Reports & Insights</span><span className="dropdown-item-subtitle">Actionable analytics</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'visual-model')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Visual Application Model</span><span className="dropdown-item-subtitle">Navigate your app visually</span></div>
                      </button>
                      <button type="button" onClick={(e) => handleNavClick(e, 'no-vendor-lock-in')} className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">No Vendor Lock-In</span><span className="dropdown-item-subtitle">Export and run anywhere</span></div>
                      </button>
                    </div>
                  </li>
                  <li className="nav-dropdown">
                    <button type="button" onClick={(e) => handleNavClick(e, 'how-it-works')}>How It Works <span className="dropdown-arrow">▾</span></button>
                    <div className="nav-dropdown-menu how-it-works-dropdown">
                      <div className="cycle-visualization">
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                          </div>
                          <span className="cycle-label">Discover</span>
                          <span className="cycle-desc">AI explores your app</span>
                        </div>
                        <div className="cycle-arrow">→</div>
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 5v14M5 12h14"/></svg>
                          </div>
                          <span className="cycle-label">Create</span>
                          <span className="cycle-desc">Generate test cases</span>
                        </div>
                        <div className="cycle-arrow">→</div>
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                          </div>
                          <span className="cycle-label">Run</span>
                          <span className="cycle-desc">Execute anywhere</span>
                        </div>
                        <div className="cycle-arrow">→</div>
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3v18h18"/><path d="M18 17V9M13 17V5M8 17v-3"/></svg>
                          </div>
                          <span className="cycle-label">Analyze</span>
                          <span className="cycle-desc">Identify failures</span>
                        </div>
                        <div className="cycle-arrow">→</div>
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z"/></svg>
                          </div>
                          <span className="cycle-label">Fix</span>
                          <span className="cycle-desc">Auto bug fixes</span>
                        </div>
                      </div>
                      <button type="button" className="cycle-cta" onClick={(e) => handleNavClick(e, 'how-it-works')}>
                        Learn more about the cycle →
                      </button>
                    </div>
                  </li>
                  <li className="nav-dropdown">
                    <button type="button" onClick={(e) => handleNavClick(e, 'pricing')}>Pricing <span className="dropdown-arrow">▾</span></button>
                    <div className="nav-dropdown-menu pricing-dropdown">
                      <div className="pricing-plans">
                        <button type="button" className="pricing-plan-item" onClick={(e) => handleNavClick(e, 'pricing')}>
                          <div className="pricing-plan-header">
                            <span className="pricing-plan-name">Free</span>
                            <span className="pricing-plan-desc">200 credits/mo + 1,500 welcome · Up to 3 apps</span>
                          </div>
                          <span className="pricing-plan-price">€0</span>
                        </button>
                        <button type="button" className="pricing-plan-item" onClick={(e) => handleNavClick(e, 'pricing')}>
                          <div className="pricing-plan-header">
                            <span className="pricing-plan-name">Starter</span>
                            <span className="pricing-plan-desc">3,000 credits/mo · Up to 5 apps</span>
                          </div>
                          <span className="pricing-plan-price">€49<span className="pricing-plan-period">/mo</span></span>
                        </button>
                        <button type="button" className="pricing-plan-item popular" onClick={(e) => handleNavClick(e, 'pricing')}>
                          <div className="pricing-plan-header">
                            <span className="pricing-plan-name">Professional<span className="pricing-plan-badge">Popular</span></span>
                            <span className="pricing-plan-desc">20,000 credits/mo · Web + Desktop</span>
                          </div>
                          <span className="pricing-plan-price">€599<span className="pricing-plan-period">/mo</span></span>
                        </button>
                        <button type="button" className="pricing-plan-item" onClick={(e) => handleNavClick(e, 'pricing')}>
                          <div className="pricing-plan-header">
                            <span className="pricing-plan-name">Enterprise</span>
                            <span className="pricing-plan-desc">Unlimited · On-premise</span>
                          </div>
                          <span className="pricing-plan-price">Custom</span>
                        </button>
                      </div>
                      <button type="button" className="cycle-cta" onClick={(e) => handleNavClick(e, 'pricing')}>
                        Compare all plans →
                      </button>
                    </div>
                  </li>
                  <li className="nav-dropdown">
                    <button type="button" onClick={() => handleDocumentationClick()}>Docs <span className="dropdown-arrow">▾</span></button>
                    <div className="nav-dropdown-menu docs-dropdown">
                      <div className="docs-items">
                        <button type="button" className="dropdown-item" onClick={() => handleDocumentationClick('web-overview')}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Web Applications</span><span className="dropdown-item-subtitle">CLI + Playwright testing</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={() => handleDocumentationClick('windows-overview')}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Windows Desktop</span><span className="dropdown-item-subtitle">Native app testing</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={() => handleDocumentationClick('rest-overview')}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">REST API</span><span className="dropdown-item-subtitle">Swagger/OpenAPI testing</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={() => handleDocumentationClick('soap-overview')}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">SOAP Services</span><span className="dropdown-item-subtitle">WSDL testing</span></div>
                        </button>
                      </div>
                      <button type="button" className="cycle-cta" onClick={() => handleDocumentationClick()}>
                        View all documentation →
                      </button>
                    </div>
                  </li>
                  <li><Link to="/blog">Blog</Link></li>
                  <li className="nav-dropdown">
                    <button type="button">Contact <span className="dropdown-arrow">▾</span></button>
                    <div className="nav-dropdown-menu contact-dropdown">
                      <div className="contact-items">
                        <button type="button" className="dropdown-item" onClick={handleScheduleDemoClick}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Schedule a Demo</span><span className="dropdown-item-subtitle">See Qate AI in action</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={handleContactSalesClick}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Contact Sales</span><span className="dropdown-item-subtitle">Talk to our team</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={handleContactSupportClick}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Contact Support</span><span className="dropdown-item-subtitle">For existing customers</span></div>
                        </button>
                      </div>
                    </div>
                  </li>
                </>
              ) : (
                // On other pages, link back to homepage sections
                <>
                  <li className="nav-dropdown">
                    <Link to="/#features">Features <span className="dropdown-arrow">▾</span></Link>
                    <div className="nav-dropdown-menu">
                      <Link to="/#conversational-exploration" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 9h8M8 13h4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Conversational Test Creation</span><span className="dropdown-item-subtitle">Chat naturally to create tests</span></div>
                      </Link>
                      <Link to="/#windows-desktop" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Windows Desktop Testing</span><span className="dropdown-item-subtitle">Test native Windows applications</span></div>
                      </Link>
                      <Link to="/#ai-suggestions" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Test Suggestions</span><span className="dropdown-item-subtitle">Smart recommendations for coverage</span></div>
                      </Link>
                      <Link to="/#discovery" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Discovery</span><span className="dropdown-item-subtitle">Auto-map your application</span></div>
                      </Link>
                      <Link to="/#ai-test-healing" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Test Healing</span><span className="dropdown-item-subtitle">Self-repairing test automation</span></div>
                      </Link>
                      <Link to="/#ai-bug-detection" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2l1.88 1.88M14.12 3.88L16 2M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"/><path d="M12 20c-3.3 0-6-2.7-6-6v-3a6 6 0 0 1 12 0v3c0 3.3-2.7 6-6 6Z"/><path d="M12 20v-9M6.53 9C4.6 8.8 3 7.1 3 5M6 13H3M6 17l-3 1M17.47 9c1.93-.2 3.53-1.9 3.53-4M18 13h3M18 17l3 1"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Bug Detection</span><span className="dropdown-item-subtitle">Automatically find issues</span></div>
                      </Link>
                      <Link to="/#ai-bug-solving" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Bug Solving</span><span className="dropdown-item-subtitle">Get fix suggestions instantly</span></div>
                      </Link>
                      <Link to="/#github-copilot" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/><circle cx="12" cy="12" r="4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">AI Coding Agent</span><span className="dropdown-item-subtitle">Copilot & Duo bugfix automation</span></div>
                      </Link>
                      <Link to="/#pr-processing" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M6 21V9a9 9 0 0 0 9 9"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Smart PR / MR Processing</span><span className="dropdown-item-subtitle">AI-driven test impact analysis</span></div>
                      </Link>
                      <Link to="/#knowledge-base" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8M8 11h8M8 15h4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Knowledge Base</span><span className="dropdown-item-subtitle">Centralized app understanding</span></div>
                      </Link>
                      <Link to="/#mfa-totp" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Multi-Factor Auth</span><span className="dropdown-item-subtitle">TOTP & authenticator testing</span></div>
                      </Link>
                      <Link to="/#enterprise-integrations" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Enterprise Integrations</span><span className="dropdown-item-subtitle">Connect your existing tools</span></div>
                      </Link>
                      <Link to="/#reports-insights" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3v18h18"/><path d="M18 17V9M13 17V5M8 17v-3"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Reports & Insights</span><span className="dropdown-item-subtitle">Actionable analytics</span></div>
                      </Link>
                      <Link to="/#visual-model" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">Visual Application Model</span><span className="dropdown-item-subtitle">Navigate your app visually</span></div>
                      </Link>
                      <Link to="/#no-vendor-lock-in" className="dropdown-item">
                        <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                        <div className="dropdown-item-text"><span className="dropdown-item-title">No Vendor Lock-In</span><span className="dropdown-item-subtitle">Export and run anywhere</span></div>
                      </Link>
                    </div>
                  </li>
                  <li className="nav-dropdown">
                    <Link to="/#how-it-works">How It Works <span className="dropdown-arrow">▾</span></Link>
                    <div className="nav-dropdown-menu how-it-works-dropdown">
                      <div className="cycle-visualization">
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                          </div>
                          <span className="cycle-label">Discover</span>
                          <span className="cycle-desc">AI explores your app</span>
                        </div>
                        <div className="cycle-arrow">→</div>
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 5v14M5 12h14"/></svg>
                          </div>
                          <span className="cycle-label">Create</span>
                          <span className="cycle-desc">Generate test cases</span>
                        </div>
                        <div className="cycle-arrow">→</div>
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                          </div>
                          <span className="cycle-label">Run</span>
                          <span className="cycle-desc">Execute anywhere</span>
                        </div>
                        <div className="cycle-arrow">→</div>
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3v18h18"/><path d="M18 17V9M13 17V5M8 17v-3"/></svg>
                          </div>
                          <span className="cycle-label">Analyze</span>
                          <span className="cycle-desc">Identify failures</span>
                        </div>
                        <div className="cycle-arrow">→</div>
                        <div className="cycle-step">
                          <div className="cycle-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z"/></svg>
                          </div>
                          <span className="cycle-label">Fix</span>
                          <span className="cycle-desc">Auto bug fixes</span>
                        </div>
                      </div>
                      <Link to="/#how-it-works" className="cycle-cta">
                        Learn more about the cycle →
                      </Link>
                    </div>
                  </li>
                  <li className="nav-dropdown">
                    <Link to="/#pricing">Pricing <span className="dropdown-arrow">▾</span></Link>
                    <div className="nav-dropdown-menu pricing-dropdown">
                      <div className="pricing-plans">
                        <Link to="/#pricing" className="pricing-plan-item">
                          <div className="pricing-plan-header">
                            <span className="pricing-plan-name">Free</span>
                            <span className="pricing-plan-desc">200 credits/mo + 1,500 welcome · Up to 3 apps</span>
                          </div>
                          <span className="pricing-plan-price">€0</span>
                        </Link>
                        <Link to="/#pricing" className="pricing-plan-item">
                          <div className="pricing-plan-header">
                            <span className="pricing-plan-name">Starter</span>
                            <span className="pricing-plan-desc">3,000 credits/mo · Up to 5 apps</span>
                          </div>
                          <span className="pricing-plan-price">€49<span className="pricing-plan-period">/mo</span></span>
                        </Link>
                        <Link to="/#pricing" className="pricing-plan-item popular">
                          <div className="pricing-plan-header">
                            <span className="pricing-plan-name">Professional<span className="pricing-plan-badge">Popular</span></span>
                            <span className="pricing-plan-desc">20,000 credits/mo · Web + Desktop</span>
                          </div>
                          <span className="pricing-plan-price">€599<span className="pricing-plan-period">/mo</span></span>
                        </Link>
                        <Link to="/#pricing" className="pricing-plan-item">
                          <div className="pricing-plan-header">
                            <span className="pricing-plan-name">Enterprise</span>
                            <span className="pricing-plan-desc">Unlimited · On-premise</span>
                          </div>
                          <span className="pricing-plan-price">Custom</span>
                        </Link>
                      </div>
                      <Link to="/#pricing" className="cycle-cta">
                        Compare all plans →
                      </Link>
                    </div>
                  </li>
                  <li className="nav-dropdown">
                    <button type="button" onClick={() => handleDocumentationClick()}>Docs <span className="dropdown-arrow">▾</span></button>
                    <div className="nav-dropdown-menu docs-dropdown">
                      <div className="docs-items">
                        <button type="button" className="dropdown-item" onClick={() => handleDocumentationClick('web-overview')}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Web Applications</span><span className="dropdown-item-subtitle">CLI + Playwright testing</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={() => handleDocumentationClick('windows-overview')}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Windows Desktop</span><span className="dropdown-item-subtitle">Native app testing</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={() => handleDocumentationClick('rest-overview')}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">REST API</span><span className="dropdown-item-subtitle">Swagger/OpenAPI testing</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={() => handleDocumentationClick('soap-overview')}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">SOAP Services</span><span className="dropdown-item-subtitle">WSDL testing</span></div>
                        </button>
                      </div>
                      <button type="button" className="cycle-cta" onClick={() => handleDocumentationClick()}>
                        View all documentation →
                      </button>
                    </div>
                  </li>
                  <li><Link to="/blog">Blog</Link></li>
                  <li className="nav-dropdown">
                    <button type="button">Contact <span className="dropdown-arrow">▾</span></button>
                    <div className="nav-dropdown-menu contact-dropdown">
                      <div className="contact-items">
                        <button type="button" className="dropdown-item" onClick={handleScheduleDemoClick}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Schedule a Demo</span><span className="dropdown-item-subtitle">See Qate AI in action</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={handleContactSalesClick}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Contact Sales</span><span className="dropdown-item-subtitle">Talk to our team</span></div>
                        </button>
                        <button type="button" className="dropdown-item" onClick={handleContactSupportClick}>
                          <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"/></svg>
                          <div className="dropdown-item-text"><span className="dropdown-item-title">Contact Support</span><span className="dropdown-item-subtitle">For existing customers</span></div>
                        </button>
                      </div>
                    </div>
                  </li>
                </>
              )}
            </ul>

            <div className="nav-actions desktop-nav">
              <button type="button" className="signin-link" onClick={handleSignInClick}>Sign In</button>
              <button type="button" className="cta-button" onClick={handleCTAClick}>Get Started Free</button>
            </div>

            {/* Hamburger Menu Button (mobile only, right-aligned next to logo) */}
            <button
              className={`hamburger-menu ${isMobileMenuOpen ? 'open' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Overlay */}
        <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} />
        
        {/* Mobile Navigation Menu */}
        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-links">
            {isHomePage ? (
              // On homepage, use smooth scroll
              <>
                <li><button type="button" onClick={(e) => handleNavClick(e, 'features')}>Features</button></li>
                <li><button type="button" onClick={(e) => handleNavClick(e, 'how-it-works')}>How It Works</button></li>
                <li><button type="button" onClick={(e) => handleNavClick(e, 'pricing')}>Pricing</button></li>
                <li><Link to="/documentation" onClick={() => setIsMobileMenuOpen(false)}>Docs</Link></li>
                <li><Link to="/blog" onClick={() => setIsMobileMenuOpen(false)}>Blog</Link></li>
                <li className="mobile-contact-section">
                  <span className="mobile-section-label">Contact</span>
                  <button type="button" onClick={handleScheduleDemoClick}>Schedule a Demo</button>
                  <button type="button" onClick={handleContactSalesClick}>Contact Sales</button>
                  <button type="button" onClick={handleContactSupportClick}>Contact Support</button>
                </li>
              </>
            ) : (
              // On other pages, link back to homepage sections
              <>
                <li><Link to="/#features" onClick={() => setIsMobileMenuOpen(false)}>Features</Link></li>
                <li><Link to="/#how-it-works" onClick={() => setIsMobileMenuOpen(false)}>How It Works</Link></li>
                <li><Link to="/#pricing" onClick={() => setIsMobileMenuOpen(false)}>Pricing</Link></li>
                <li><Link to="/documentation" onClick={() => setIsMobileMenuOpen(false)}>Docs</Link></li>
                <li><Link to="/blog" onClick={() => setIsMobileMenuOpen(false)}>Blog</Link></li>
                <li className="mobile-contact-section">
                  <span className="mobile-section-label">Contact</span>
                  <button type="button" onClick={handleScheduleDemoClick}>Schedule a Demo</button>
                  <button type="button" onClick={handleContactSalesClick}>Contact Sales</button>
                  <button type="button" onClick={handleContactSupportClick}>Contact Support</button>
                </li>
              </>
            )}
          </ul>
          
          <div className="mobile-nav-actions">
            <button type="button" className="mobile-signin-link" onClick={handleSignInClick}>Sign In</button>
            <button type="button" className="mobile-cta-button" onClick={handleCTAClick}>Get Started Free</button>
          </div>
        </div>
      </nav>
      
      {showScheduleDemoModal && <ScheduleDemoModal onClose={() => setShowScheduleDemoModal(false)} />}
      {showContactSalesModal && <ContactSalesModal onClose={() => setShowContactSalesModal(false)} />}
      {showContactSupportModal && <ContactSupportModal onClose={() => setShowContactSupportModal(false)} />}
    </>
  );
};

export default Navigation;